import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Checkbox, Dialog, DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, Typography } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import cx from 'classnames';
import { FormikProps } from 'formik';
import React, { useRef, useState } from 'react';
import PrismaZoom from 'react-prismazoom';
import { translate } from '../../../../common/intl';
import { AsyncState, AsyncStatus } from '../../../../state/types';
import { styles as viewFileStyles } from '../../../components/common/view.file/utils/styles';
import { useStyles } from './WhiteLabelingManagement.styles';

// S3 keys percent-encode spaces etc; decode for display so blanks don't show as "%20"
const getFileNameFromUrl = (url: string) => {
  const name = url.split('/').pop() || '';
  try {
    return decodeURIComponent(name);
  } catch (e) {
    return name;
  }
};

interface Props extends FormikProps<any>{
  onLogoChange: (event: React.FormEvent<HTMLInputElement>) => void
  onAvatarChange: (event: React.FormEvent<HTMLInputElement>) => void
  onPoLogoChange: (event: React.FormEvent<HTMLInputElement>) => void
  onPoLogoSameAsLogo: (checked: boolean) => void
  onDownload: (s3link: string) => void
  asyncState: AsyncState
  asyncS3linkState: AsyncState
  whiteLabel: { logo: string, avatar: string, poLogo?: string }
}

const WhiteLabelingManagementPresenter: React.FunctionComponent<Props> = ({
  dirty,
  resetForm,
  initialValues,
  onLogoChange,
  onAvatarChange,
  onPoLogoChange,
  onPoLogoSameAsLogo,
  onDownload,
  asyncState,
  asyncS3linkState,
  whiteLabel,
}) => {
  const classes = useStyles();
  const viewFileClasses = viewFileStyles();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const poLogoInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const isPoLogoSameAsLogo = Boolean(whiteLabel.logo) && whiteLabel.poLogo === whiteLabel.logo;

  const handleUploadClick = (inputRef: React.RefObject<HTMLInputElement>) => {
    inputRef.current?.click();
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLElement>, inputRef: React.RefObject<HTMLInputElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    const isSupportedType = file && ['image/png', 'image/jpeg'].includes(file.type);
    if (!file || !isSupportedType || !inputRef.current || isLoading) {
      return;
    }

    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    inputRef.current.files = dataTransfer.files;
    inputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const isLoading = asyncState?.status === AsyncStatus.Active || asyncS3linkState?.status === AsyncStatus.Active;

  const renderUploadSection = (
    title: string,
    sizeLabel: string,
    inputRef: React.RefObject<HTMLInputElement>,
    onChange: (event: React.FormEvent<HTMLInputElement>) => void,
    s3link: string | undefined,
  ) => {
    const hasCurrentFile = Boolean(s3link);

    return (
      <Box mb={2}>
        <Typography className={classes.label}>
          {title}{' '}
          <span className={classes.sizeLabel}>{sizeLabel}</span>
        </Typography>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg"
          onChange={onChange}
          className={classes.fileInput}
          title={translate('administration.company.information.whiteLabeling.chooseFile')}
          aria-label={translate('administration.company.information.whiteLabeling.chooseFile')}
          disabled={isLoading}
        />

        <Box
          className={classes.uploadArea}
          onClick={() => handleUploadClick(inputRef)}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, inputRef)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleUploadClick(inputRef);
            }
          }}
          aria-label={translate(
            hasCurrentFile
              ? 'administration.company.information.whiteLabeling.replaceAriaLabel'
              : 'administration.company.information.whiteLabeling.uploadAriaLabel',
            { title: title.toLowerCase() },
          )}
        >
          <FontAwesomeIcon icon={solid('copy')} className={classes.uploadIcon} />
          <Typography className={classes.uploadText}>
            {translate('administration.company.information.whiteLabeling.dragDropPrefix')}{' '}
            <span className={classes.chooseFileLink}>
              {translate('administration.company.information.whiteLabeling.chooseFile')}
            </span>{' '}
            {translate(
              hasCurrentFile
                ? 'administration.company.information.whiteLabeling.toReplace'
                : 'administration.company.information.whiteLabeling.toUpload',
            )}
          </Typography>
          <Typography className={classes.uploadSubtext}>
            {translate('administration.company.information.whiteLabeling.fileTypes')}
          </Typography>
        </Box>

        <Box mt={2}>
          <Typography className={classes.currentFileLabel}>
            {translate('administration.company.information.whiteLabeling.currentFile')}
          </Typography>
          <Typography className={classes.currentFileName}>
            {s3link ? (
              <a
                href={s3link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  setPreviewUrl(s3link);
                }}
              >
                {getFileNameFromUrl(s3link)}
              </a>
            ) : (
              translate('administration.company.information.whiteLabeling.noFileUploaded')
            )}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {renderUploadSection(
        translate('administration.company.information.whiteLabeling.logo.upload'),
        translate('administration.company.information.whiteLabeling.logo.recommendedSize'),
        logoInputRef,
        onLogoChange,
        whiteLabel.logo,
      )}
      <Divider className={classes.divider} />
      {renderUploadSection(
        translate('administration.company.information.whiteLabeling.avatar.upload'),
        translate('administration.company.information.whiteLabeling.avatar.recommendedSize'),
        avatarInputRef,
        onAvatarChange,
        whiteLabel.avatar,
      )}
      <Divider className={classes.divider} />
      <Box>
        {renderUploadSection(
          translate('administration.company.information.whiteLabeling.poLogo.upload'),
          translate('administration.company.information.whiteLabeling.poLogo.recommendedSize'),
          poLogoInputRef,
          onPoLogoChange,
          whiteLabel.poLogo,
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={isPoLogoSameAsLogo}
              onChange={(event) => onPoLogoSameAsLogo(event.target.checked)}
              disabled={isLoading || !whiteLabel.logo}
            />
          }
          label={translate('administration.company.information.whiteLabeling.poLogo.sameAsLogo')}
        />
      </Box>

      <Dialog
        onClose={() => setPreviewUrl(null)}
        open={Boolean(previewUrl)}
        classes={{ paper: viewFileClasses.paper }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle className={cx(viewFileClasses.title, classes.previewTitle)}>
          {previewUrl && getFileNameFromUrl(previewUrl)}
          <IconButton
            onClick={() => previewUrl && onDownload(previewUrl)}
            aria-label={translate('administration.company.information.whiteLabeling.download')}
            className={classes.previewDownloadButton}
          >
            <GetAppIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          classes={{ root: viewFileClasses.contentRoot }}
          onClick={(e) => {
            if ((e.target as HTMLElement).tagName === 'DIV') {
              setPreviewUrl(null);
            }
          }}
        >
          {previewUrl && (
            <PrismaZoom className={viewFileClasses.zoom} topBoundary={180}>
              <img src={previewUrl} alt={previewUrl} className={classes.previewImage} />
            </PrismaZoom>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WhiteLabelingManagementPresenter;
